/**
 * UTC-anchored calendar-day math. A booking's `date` is stored as UTC-midnight of the CENTRE's day,
 * so the centre's calendar day is exactly the UTC calendar day of `date`. Deriving days in the
 * browser's LOCAL timezone (getDay/getDate, date-fns format/startOfDay) shifts bookings onto the
 * wrong day for viewers whose browser TZ differs from the centre's — the travelling-member bug.
 * These helpers keep every day decision in UTC so the calendar is viewer-timezone independent.
 */

/** UTC calendar day as "yyyy-MM-dd". */
export const utcDayKey = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * UTC-midnight of the calendar day that `d` represents in the viewer's LOCAL timezone.
 *
 * react-day-picker's `onSelect` (and a bare `new Date()`) hand back LOCAL-time instants: clicking
 * "21 August" yields local-midnight of the 21st, which under a viewer TZ ahead of UTC (e.g. BST)
 * is 23:00Z on the 20th — a UTC calendar day BEFORE the day the user actually picked. Every
 * UTC-anchored consumer (`formatUtcDayHeader`, `utcDayBounds`, `utcDayKey`) would then read the
 * wrong day. Normalise picked/local days through this at the boundary so the chosen calendar day
 * is preserved as UTC-midnight, matching the day-only convention the rest of the calendar assumes.
 */
export const localDayToUtcMidnight = (d: Date): Date =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));

/**
 * The inverse of `localDayToUtcMidnight`: LOCAL-midnight of the UTC calendar day `d` represents.
 * Feed UTC-anchored days through this before handing them to react-day-picker (`selected`,
 * `defaultMonth`, bounds) or date-fns `format`, both of which read the LOCAL day — west of UTC a
 * raw UTC-midnight instant is still the previous evening locally.
 */
export const utcMidnightToLocalDay = (d: Date): Date => new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

/** True when two instants fall on the same UTC calendar day. */
export const sameUtcDay = (a: Date, b: Date): boolean => utcDayKey(a) === utcDayKey(b);

/** UTC-midnight and UTC end-of-day for the UTC day containing `d`. */
export const utcDayBounds = (d: Date): { start: Date; end: Date } => {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  return {
    start: new Date(Date.UTC(y, m, day, 0, 0, 0, 0)),
    end: new Date(Date.UTC(y, m, day, 23, 59, 59, 999)),
  };
};

/** UTC day-of-week (0=Sunday … 6=Saturday). */
export const utcWeekday = (d: Date): number => d.getUTCDay();

/** Monday-based start of the UTC week containing `d`, at UTC-midnight. */
export const startOfUtcWeek = (d: Date): Date => {
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day; // Sunday → back 6; else back to Monday
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diff, 0, 0, 0, 0));
};

/** The 7 UTC-midnight day dates of the week starting at `weekStart`. */
export const utcWeekDates = (weekStart: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) =>
    new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + i, 0, 0, 0, 0)),
  );

/** "Tue 5 May" style header, rendered in UTC so the label matches the UTC day. */
export const formatUtcDayHeader = (d: Date): string => {
  const part = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(d);
  const dayName = part({ weekday: "long" }).substring(0, 3);
  return `${dayName} ${d.getUTCDate()} ${part({ month: "short" })}`;
};
